// Section 2 (Additional Certifications) for
// the A2 / A3 / A4 analyst tabs.
//
// Each row renders identically to a Section 1 criteria-checklist row: a status
// chip (PENDING / CERTIFIED / DECLINED / ESCALATED), three action buttons
// (CERTIFY / DECLINE / ESCALATE), an OPEN FORM modal, and an admin "Edit…"
// kebab.
//
// The cert universe (title + blurb + prose) now comes LIVE from the DB
// UnderwritingCertificationTemplate table (admin-editable), not a frontend
// static array; the fetched UnderwritingCertification rows supply per-cert
// status/state.

import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { Card, Badge, Button } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import {
  useCertificationTemplatesByLane,
  useUnderwritingCertifications,
} from "@/hooks/dealFlow/useDealFlow";
import type {
  CriteriaLane,
  UnderwritingCertificationDto,
} from "@/api/dealFlow/types";
import { CertificationItemRow } from "./CertificationItemRow";
import { CertificationCatalogCreateModal } from "./CertificationCatalogCreateModal";
import { tabForAnalystLane } from "./certConfigs";
import type { CertConfig } from "./types";

interface AdditionalCertificationsSectionProps {
  dealId: string;
  lane: CriteriaLane;
  /** When true, hides every write affordance. Status rendering is kept so
   *  non-owners can audit the lane's progress. */
  readOnly?: boolean;
}

/** A cert is "complete" once it has reached any terminal status
 *  (Certified / Declined / Escalated) — mirrors Section 1's terminal
 *  semantics. A row that is missing or "Pending" is incomplete. */
function isTerminal(row: UnderwritingCertificationDto | undefined): boolean {
  if (!row) return false;
  return (
    row.status === "Certified" ||
    row.status === "Declined" ||
    row.status === "Escalated"
  );
}

export function AdditionalCertificationsSection({
  dealId,
  lane,
  readOnly = false,
}: AdditionalCertificationsSectionProps) {
  const auth = useAuth();
  const tab = tabForAnalystLane(lane);
  const templatesQuery = useCertificationTemplatesByLane(lane);
  const certsQuery = useUnderwritingCertifications(dealId, tab, 2);
  const [createOpen, setCreateOpen] = useState(false);

  // ADMIN-only "Add certification" header. Only the analyst lanes (A2/A3/A4)
  // have a Section 2 — A1 (Underwriting) has none, so never offer Add there.
  const canAdd =
    !readOnly &&
    auth.hasRoleCode("ADMIN") &&
    (lane === "A2" || lane === "A3" || lane === "A4");

  const addButton = canAdd ? (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => setCreateOpen(true)}
      data-testid="add-certification-button"
    >
      <Plus size={14} aria-hidden /> Add certification
    </Button>
  ) : null;

  const createModal = canAdd ? (
    <CertificationCatalogCreateModal
      open={createOpen}
      onClose={() => setCreateOpen(false)}
      lane={lane}
    />
  ) : null;

  const templates = useMemo(
    () => templatesQuery.data ?? [],
    [templatesQuery.data],
  );

  const rowByCode = useMemo(() => {
    const map = new Map<string, UnderwritingCertificationDto>();
    for (const c of certsQuery.data ?? []) {
      map.set(c.certificationCode, c);
    }
    return map;
  }, [certsQuery.data]);

  const totalTerminal = templates.filter((t) =>
    isTerminal(rowByCode.get(t.certificationCode)),
  ).length;
  const allTerminal = templates.length > 0 && totalTerminal === templates.length;

  if (templatesQuery.isLoading || certsQuery.isLoading) {
    return (
      <Card>
        <div
          className="flex flex-col gap-space-2"
          data-testid="addl-certs-skeleton"
        >
          <div className="h-6 bg-white-warm animate-pulse rounded-md" />
          <div className="h-16 bg-white-warm animate-pulse rounded-md" />
          <div className="h-16 bg-white-warm animate-pulse rounded-md" />
        </div>
      </Card>
    );
  }

  if (templates.length === 0) {
    return (
      <Card>
        <div className="flex items-start justify-between gap-space-3">
          <p className="text-small text-gray-mid italic m-0">
            No additional certifications required for this lane.
          </p>
          {addButton}
        </div>
        {createModal}
      </Card>
    );
  }

  return (
    <Card>
      <header className="mb-space-3 flex items-start justify-between gap-space-3">
        <div>
          <h3 className="font-display text-h4 text-onyx m-0">
            Section 2 — Additional Certifications ({lane})
          </h3>
          <p className="text-caption text-gray-mid m-0">
            Certify, decline, or escalate each methodology certification. Open
            the form to record your severity assessment, report attestation,
            and justification.
          </p>
        </div>
        <div className="flex items-center gap-space-2">
          {allTerminal ? (
            <Badge status="gold">
              All {templates.length} certifications resolved
            </Badge>
          ) : (
            <Badge status="archived">
              {totalTerminal} / {templates.length} resolved
            </Badge>
          )}
          {addButton}
        </div>
      </header>

      <ul
        className="flex flex-col gap-space-2 m-0 p-0 list-none"
        data-testid={`addl-certs-${lane}`}
      >
        {templates.map((template) => (
          <CertificationItemRow
            key={template.certificationCode}
            dealId={dealId}
            tab={tab}
            cert={{
              certificationCode: template.certificationCode,
              title: template.title,
              description: template.shortDescription,
            }}
            template={template}
            row={rowByCode.get(template.certificationCode) ?? null}
            readOnly={readOnly}
          />
        ))}
      </ul>

      {createModal}
    </Card>
  );
}

/** True when every required cert's code is among the resolved codes. Only its
 *  tests call it: Section 3 gates its Issue Pass/Fail button through
 *  useAnalystTabState (analystSectionTwo.ts). */
export function isAllAddlCertsSigned(
  resolvedCodes: ReadonlyArray<string>,
  required: ReadonlyArray<CertConfig>,
): boolean {
  if (required.length === 0) return true;
  const resolved = new Set(resolvedCodes);
  return required.every((c) => resolved.has(c.certificationCode));
}
